import { io, Socket } from 'socket.io-client';

interface TournamentUpdatePayload {
  tournamentId: string;
  updateType: 'result' | 'pairings' | 'standings' | 'general' | 'registration';
  matchId?: string;
  registrationId?: string;
}

type UpdateHandler = (payload: TournamentUpdatePayload) => void;

class WebSocketClient {
  private socket: Socket | null = null;
  private connected: boolean = false;
  private subscribedTournaments: Set<string> = new Set();
  private updateHandlers: Set<UpdateHandler> = new Set();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;

  /**
   * Initialize the WebSocket connection
   */
  connect(): void {
    if (this.socket) {
      console.log('[WebSocket] Already connected');
      return;
    }

    // Determine the WebSocket URL based on current page
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}`;

    console.log('[WebSocket] Connecting to:', wsUrl);

    this.socket = io(wsUrl, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      transports: ['websocket', 'polling'], // Try WebSocket first, fall back to polling
    });

    this.setupEventHandlers();
  }

  /**
   * Set up WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected successfully');
      this.connected = true;
      this.reconnectAttempts = 0;

      // Resubscribe to all tournaments on reconnect
      if (this.subscribedTournaments.size > 0) {
        console.log('[WebSocket] Resubscribing to tournaments after reconnect');
        this.subscribedTournaments.forEach(tournamentId => {
          this.socket?.emit('subscribe:tournament', tournamentId);
        });
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.connected = false;

      if (reason === 'io server disconnect') {
        // Server disconnected us, try to reconnect manually
        this.reconnectAttempts++;
        if (this.reconnectAttempts <= this.maxReconnectAttempts) {
          console.log(`[WebSocket] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => this.socket?.connect(), 1000);
        }
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.connected = false;
    });

    this.socket.on('subscribed', ({ tournamentId }: { tournamentId: string }) => {
      console.log('[WebSocket] Subscribed to tournament:', tournamentId);
    });

    this.socket.on('unsubscribed', ({ tournamentId }: { tournamentId: string }) => {
      console.log('[WebSocket] Unsubscribed from tournament:', tournamentId);
    });

    this.socket.on('tournament:update', (payload: TournamentUpdatePayload) => {
      console.log('[WebSocket] Received tournament update:', payload);
      this.notifyHandlers(payload);
    });
  }

  /**
   * Subscribe to a tournament's updates
   */
  subscribeTournament(tournamentId: string): void {
    if (!tournamentId) {
      console.warn('[WebSocket] Cannot subscribe - invalid tournament ID');
      return;
    }

    if (!this.socket) {
      this.connect();
    }

    if (this.subscribedTournaments.has(tournamentId)) {
      console.log('[WebSocket] Already subscribed to tournament:', tournamentId);
      return;
    }

    this.subscribedTournaments.add(tournamentId);

    if (this.connected) {
      this.socket?.emit('subscribe:tournament', tournamentId);
    } else {
      // Will subscribe when connection is established
      console.log('[WebSocket] Will subscribe to tournament when connected:', tournamentId);
    }
  }

  /**
   * Unsubscribe from a tournament's updates
   */
  unsubscribeTournament(tournamentId: string): void {
    if (!tournamentId || !this.subscribedTournaments.has(tournamentId)) {
      return;
    }

    this.subscribedTournaments.delete(tournamentId);
    this.socket?.emit('unsubscribe:tournament', tournamentId);
  }

  /**
   * Register a handler for tournament updates
   */
  onUpdate(handler: UpdateHandler): void {
    this.updateHandlers.add(handler);
  }

  /**
   * Unregister a handler for tournament updates
   */
  offUpdate(handler: UpdateHandler): void {
    this.updateHandlers.delete(handler);
  }

  /**
   * Notify all registered handlers of an update
   */
  private notifyHandlers(payload: TournamentUpdatePayload): void {
    this.updateHandlers.forEach(handler => {
      try {
        handler(payload);
      } catch (error) {
        console.error('[WebSocket] Error in update handler:', error);
      }
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Disconnect the WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[WebSocket] Disconnecting...');
      this.subscribedTournaments.clear();
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }
}

// Export singleton instance
export const websocketClient = new WebSocketClient();

// Auto-connect on page load (will be lazy-initialized on first subscribe)
// This ensures we don't create unnecessary connections for pages that don't need real-time updates
